import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { landingMeta } from "../lib/landingPages";

const OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_d18f6833-d0fb-4eb1-8e13-5e5acd0eae4f/artifacts/itc1q8k2_58B4A2FB-2C38-4220-8170-4B53EED4B172.png";

const KEYWORDS =
  "autobedrijf Arnhem, occasions Arnhem, auto kopen Arnhem, APK Arnhem, onderhoud auto Arnhem, auto import, afspraak garage Arnhem, MP Autobedrijf";

const MAP = {
  nl: {
    "/": {
      t: "MP Autobedrijf | Occasions, APK & Onderhoud in Arnhem Noord",
      d: "Betrouwbare jonge occasions, eerlijk onderhoud, APK en auto-import in Arnhem Noord. Bekijk ons aanbod of plan eenvoudig online een afspraak voor uw voertuig.",
    },
    "/occasions": {
      t: "Occasions in Arnhem | Scherp geprijsde jonge auto's — MP Autobedrijf",
      d: "Ontdek ons actuele aanbod jonge occasions in Arnhem Noord. Filter op merk, brandstof, bouwjaar en prijs. Met garantie en eerlijk advies.",
    },
    "/diensten": {
      t: "Diensten: Verkoop, Import, Onderhoud & APK | MP Autobedrijf Arnhem",
      d: "Van autoverkoop en import tot onderhoud, reparatie en APK-keuring. Vakmanschap onder één dak in Arnhem Noord. Plan direct uw afspraak.",
    },
    "/over-ons": {
      t: "Over MP Autobedrijf | Uw autobedrijf in Arnhem Noord",
      d: "MP Autobedrijf in Arnhem Noord staat voor vakmanschap, eerlijkheid en persoonlijke service. Lees meer over ons bedrijf.",
    },
    "/reviews": {
      t: "Reviews & ervaringen | MP Autobedrijf Arnhem",
      d: "Lees wat klanten zeggen over MP Autobedrijf. Gemiddeld 4,9 / 5 op basis van echte ervaringen met verkoop, onderhoud en APK.",
    },
    "/contact": {
      t: "Contact & route | MP Autobedrijf Arnhem Noord",
      d: "Neem contact op met MP Autobedrijf in Arnhem Noord. Johannes Huddeweg 14, 6827 CB Arnhem. Bel, mail of WhatsApp ons.",
    },
    "/afspraak": {
      t: "Online afspraak maken voor uw voertuig | MP Autobedrijf Arnhem",
      d: "Maak gemakkelijk online een afspraak voor APK, onderhoud, reparatie of diagnose. Kies dienst, datum en tijd — u ontvangt direct een bevestiging.",
    },
    "/inruil": {
      t: "Auto inruilen in Arnhem | Eerlijk bod — MP Autobedrijf",
      d: "Uw huidige auto inruilen? Vraag vrijblijvend een eerlijk inruilvoorstel aan bij MP Autobedrijf in Arnhem Noord.",
    },
    "/nieuws": {
      t: "Auto- & APK-tips | MP Autobedrijf Arnhem",
      d: "Praktische onderhouds-, APK- en wintertips van onze monteurs: koude start & oliedruk, bandenspanning, remmen, accu en meer.",
    },
    _default: {
      t: "MP Autobedrijf — Occasions, onderhoud & APK in Arnhem Noord",
      d: "Betrouwbare jonge occasions, onderhoud, APK en auto-import in Arnhem Noord.",
    },
  },
  en: {
    "/": {
      t: "MP Autobedrijf | Used cars, MOT & maintenance in Arnhem",
      d: "Reliable young used cars, honest maintenance, MOT and car import in Arnhem North. Browse our stock or easily book an appointment for your vehicle online.",
    },
    "/occasions": {
      t: "Used cars in Arnhem | Sharply priced young cars — MP Autobedrijf",
      d: "Discover our current range of young used cars in Arnhem North. Filter by make, fuel, year and price. With warranty and honest advice.",
    },
    "/diensten": {
      t: "Services: Sales, Import, Maintenance & MOT | MP Autobedrijf",
      d: "From car sales and import to maintenance, repair and MOT. Craftsmanship under one roof in Arnhem North. Book your appointment now.",
    },
    "/over-ons": { t: "About MP Autobedrijf | Your garage in Arnhem North", d: "MP Autobedrijf in Arnhem North stands for craftsmanship, honesty and personal service." },
    "/reviews": { t: "Reviews & experiences | MP Autobedrijf Arnhem", d: "Read what customers say about MP Autobedrijf. Rated 4.9 / 5 on average from real experiences." },
    "/contact": { t: "Contact & directions | MP Autobedrijf Arnhem North", d: "Get in touch with MP Autobedrijf. Johannes Huddeweg 14, 6827 CB Arnhem. Call, email or WhatsApp us." },
    "/afspraak": { t: "Book an appointment for your vehicle online | MP Autobedrijf", d: "Easily book online for MOT, maintenance, repair or diagnostics. Pick service, date and time — instant confirmation." },
    "/inruil": { t: "Trade in your car in Arnhem | MP Autobedrijf", d: "Want to trade in your current car? Request a fair, no-obligation trade-in offer at MP Autobedrijf." },
    "/nieuws": { t: "Car & MOT tips | MP Autobedrijf Arnhem", d: "Practical maintenance, MOT and winter tips from our mechanics: cold start & oil pressure, tyre pressure, brakes, battery and more." },
    _default: { t: "MP Autobedrijf — Used cars, maintenance & MOT in Arnhem", d: "Reliable young used cars, maintenance, MOT and car import in Arnhem North." },
  },
};

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo() {
  const { pathname } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    const table = MAP[lang] || MAP.nl;
    const slug = pathname.replace(/^\//, "");
    const meta = landingMeta(slug, lang) || table[pathname] || table._default;
    const url = window.location.origin + pathname;

    document.title = meta.t;
    document.documentElement.lang = lang;
    upsertMeta("name", "description", meta.d);
    upsertMeta("name", "keywords", KEYWORDS);
    upsertMeta("property", "og:title", meta.t);
    upsertMeta("property", "og:description", meta.d);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "MP Autobedrijf");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("property", "og:locale", lang === "en" ? "en_US" : "nl_NL");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", meta.t);
    upsertMeta("name", "twitter:description", meta.d);
    upsertMeta("name", "twitter:image", OG_IMAGE);
    upsertLink("canonical", url);
  }, [pathname, lang]);

  return null;
}
